import { useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { ApiResponse, ApiResponseNHL, useFetchData, useFetchLeagueData } from '~/core/api';
import { D3_CONTENT_SEARCH } from '~/core/constants';
import { Highlights, StoryByTag, StoryItem } from '~/core/forge/types';
import { updateLastFetchedGame, updateRightRail } from '~/redux/ducks/gamecenter';

import { BoxscoreGame, CommonGCGame, LandingGame, PlayByPlayGame, PptSprite, RightRailDetails } from './types';

const useFetchGameCenterData = <T extends CommonGCGame>(endpoint: string, gameId?: number): ApiResponseNHL<T> => {
    const dispatch = useDispatch();

    const response = useFetchLeagueData<T>(endpoint, !!gameId);

    useEffect(() => {
        const gameCenterData = response.data;
        // If we have data we will set this in our redux store. That way the right rail and scoreboard can use this
        if (gameCenterData.id) {
            dispatch(updateLastFetchedGame(gameCenterData));
        }
    }, [dispatch, response.data]);

    return response;
};

export const useGameCenterBoxscoreQuery = (gameId?: number): ApiResponseNHL<BoxscoreGame> => {
    return useFetchGameCenterData(`/gamecenter/${gameId}/boxscore`, gameId);
};

export const useGameCenterLandingQuery = (gameId?: number): ApiResponseNHL<LandingGame> =>
    useFetchGameCenterData(`/gamecenter/${gameId}/landing`, gameId);

export const useGameCenterPlayByPlayQuery = (gameId?: number): ApiResponseNHL<PlayByPlayGame> =>
    useFetchGameCenterData(`/gamecenter/${gameId}/play-by-play`, gameId);

export const useGameCenterRightRailQuery = (gameId?: number): ApiResponseNHL<RightRailDetails> => {
    const dispatch = useDispatch();

    const response = useFetchLeagueData<RightRailDetails>(`/gamecenter/${gameId}/right-rail`, gameId !== undefined);

    useEffect(() => {
        const rightRailDetails = response.data;
        if (rightRailDetails?.gameInfo) {
            dispatch(updateRightRail(rightRailDetails));
        }
    }, [dispatch, response.data]);

    return response;
};

export const useGamePreviewQuery = (gameId?: number): ApiResponse<StoryByTag> =>
    useFetchData(`${D3_CONTENT_SEARCH}/stories?tags.slug=gameid-${gameId}&tags.slug=game-preview&context.slug=nhl`);

export const useGameRecapQuery = (gameId?: number): ApiResponse<StoryByTag> =>
    useFetchData(`${D3_CONTENT_SEARCH}/stories?tags.slug=gameid-${gameId}&tags.slug=game-recap&context.slug=nhl`);

export const useGameRecapDetailsQuery = (endpoint): ApiResponse<StoryItem> => useFetchData(endpoint, !!endpoint);

export const useGameHighlightsQuery = (gameId: number): ApiResponse<Highlights> =>
    useFetchData(`${D3_CONTENT_SEARCH}/videos?context.slug=nhl&tags.slug=highlight&tags.slug=gameid-${gameId}`);

export const usePptSpriteQuery = (pptReplayUrl?: string): ApiResponse<Array<PptSprite>> =>
    useFetchData(pptReplayUrl || '', pptReplayUrl !== undefined);
